// Renn marketing content + brand assets (frontend content source)

export const BRAND = {
  name: "Renn",
  company: "WorthGrid Technologies, LLC",
  tagline: "Know your next money move.",
};

// Real Renn app screenshots (source of truth)
export const SHOTS = {
  moves: "https://customer-assets-jt897jd0.emergentagent.net/job_smart-ai-page-2/artifacts/jlpx8suy_4.webp",
  scenario: "https://customer-assets-jt897jd0.emergentagent.net/job_smart-ai-page-2/artifacts/2nfnlhv5_5.webp",
  weekly: "https://customer-assets-jt897jd0.emergentagent.net/job_smart-ai-page-2/artifacts/qpc13vco_6.webp",
  settings: "https://customer-assets-jt897jd0.emergentagent.net/job_smart-ai-page-2/artifacts/67bzw5ao_7.webp",
};

export const NAV_LINKS = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "WorthScore", href: "#worthscore" },
  { label: "Renn Moves", href: "#renn-moves" },
  { label: "Ask Renn", href: "#ask-renn" },
  { label: "Military", href: "#military" },
  { label: "Security", href: "#security" },
  { label: "FAQ", href: "#faq" },
];

export const HERO = {
  eyebrow: "Built by a veteran. Built for military. Smart enough for everyone.",
  headline: "Know your next money move.",
  copy: "Renn analyzes your financial situation, identifies what needs attention, and gives you the three actions that can make the greatest difference right now.",
  primary: "Join the Renn Beta",
  secondary: "See How Renn Works",
  trust: "No bank passwords. No Social Security numbers. Your financial information stays under your control.",
};

export const PROBLEM = {
  headline: "Most financial apps show you what happened. Renn tells you what to do next.",
  copy: "Charts and transaction lists can show where your money went. They do not always tell you what deserves your attention first. Renn turns your financial profile into clear priorities.",
  traditional: {
    title: "Traditional financial apps",
    points: [
      "Show balances and transactions",
      "Organize spending categories",
      "Leave you to determine priorities",
      "Often provide the same tools to everyone",
    ],
  },
  renn: {
    title: "Renn",
    points: [
      "Reviews your financial position",
      "Creates a WorthScore",
      "Identifies the three highest-impact actions",
      "Adjusts the plan as information changes",
    ],
  },
};

export const STEPS = [
  {
    n: 1,
    title: "Tell Renn about your money",
    copy: "Complete a guided profile covering income, expenses, savings, debt, credit, protection, military compensation, and goals.",
  },
  {
    n: 2,
    title: "WorthGrid analyzes your position",
    copy: "WorthGrid evaluates the financial areas that shape your readiness and creates a 0\u2013100 WorthScore.",
  },
  {
    n: 3,
    title: "Get your three Renn Moves",
    copy: "Renn identifies the three actions that deserve your attention first and explains how to begin.",
  },
];

export const WORTHSCORE = {
  headline: "Understand your financial momentum.",
  copy: "WorthScore turns a complicated financial picture into one clear signal.",
  score: 72,
  note: "See what is working, what needs attention, and how your position changes over time.",
  categories: [
    { label: "Cash flow health", value: 82, state: "good" },
    { label: "Emergency savings", value: 46, state: "warn" },
    { label: "Debt health", value: 38, state: "urgent" },
    { label: "Credit health", value: 71, state: "good" },
    { label: "Retirement readiness", value: 64, state: "warn" },
    { label: "Financial protection", value: 77, state: "good" },
    { label: "Goal execution", value: 59, state: "warn" },
  ],
};

export const MOVES = {
  headline: "Three moves. In priority order.",
  copy: "Renn cuts through the noise and identifies the actions that matter most right now.",
  note: "Your Renn Moves update as your income, expenses, debt, goals, and life circumstances change.",
  items: [
    {
      n: 1,
      title: "Build your starter emergency fund",
      copy: "A small cash cushion reduces your dependence on credit cards when unexpected expenses show up.",
      action: "Redirect $275 / month into a dedicated savings account.",
      impact: "$2,500 target",
      difficulty: "Easy",
      level: "easy",
    },
    {
      n: 2,
      title: "Eliminate your highest-cost debt",
      copy: "Your most expensive balance grows fastest. Clearing it first protects the rest of your plan.",
      action: "Focus extra payments on the ~36% APR balance first.",
      impact: "Save on interest",
      difficulty: "Hard",
      level: "hard",
    },
    {
      n: 3,
      title: "Capture your full retirement match",
      copy: "Contributing enough to receive your full match is one of the highest-return moves available to you.",
      action: "Increase your TSP contribution to capture the full match.",
      impact: "+ long-term growth",
      difficulty: "Medium",
      level: "medium",
    },
  ],
};

export const MISSIONS = {
  headline: "Turn priorities into measurable progress.",
  copy: "Convert Renn Moves into trackable Missions with clear targets, deadlines, milestones, and progress.",
  note: "Knowing what to do is the beginning. Tracking the work is how progress becomes real.",
  active: [
    { title: "Starter emergency fund", current: 1450, target: 2500, pct: 58, due: "Target: Sep 2026" },
    { title: "Pay down SunBit balance", current: 640, target: 1100, pct: 42, due: "Target: Nov 2026" },
  ],
  completed: [
    { title: "Open high-yield savings", pct: 100, due: "Completed Apr 2026" },
  ],
};

export const ASK_RENN = {
  headline: "Ask Renn about your plan.",
  copy: "Ask Renn uses your saved financial profile to explain priorities, compare options, and help you understand the reasoning behind your plan.",
  note: "Renn explains your options. You stay in control of every decision.",
  disclaimer: "Educational guidance and decision support, not individualized legal, tax, accounting, insurance, or investment advice.",
  questions: [
    "What should I focus on first?",
    "Which debt is costing me the most?",
    "Can I afford this purchase?",
    "How much emergency savings should I have?",
    "What happens if my income changes?",
    "Should I increase my TSP contribution?",
    "How should I prepare financially for deployment?",
  ],
};

export const WEEKLY = {
  headline: "Your plan should change when your life changes.",
  copy: "A quick weekly check-in helps Renn keep your WorthScore, Missions, and next money moves current.",
  highlights: ["Income updates", "Spending updates", "Savings updates", "Debt activity", "Goal changes"],
};

export const SCENARIO = {
  headline: "Model the outcome before you decide.",
  copy: "Explore how a potential decision could affect your cash flow, savings, debt timeline, and WorthScore.",
  note: "Projections are estimates based on the information provided.",
  examples: [
    "Increase savings",
    "Pay debt faster",
    "Reduce monthly expenses",
    "Prepare for a home purchase",
    "Handle an income change",
    "Plan for military separation",
  ],
};

export const MILITARY = {
  headline: "Built for the financial realities of military life.",
  copy: "Military pay, allowances, deployments, PCS moves, TSP decisions, separation, and retirement do not always fit neatly inside ordinary financial apps. Renn is being built to understand those situations.",
  audience: ["Active duty", "Reserve", "National Guard", "Veterans", "Military spouses"],
  topics: ["Basic pay", "BAH", "BAS", "TSP", "PCS preparation", "Deployment preparation", "Separation planning", "Retirement planning"],
  line: "Built by a veteran. Built for military. Smart enough for everyone.",
  disclaimer: "Renn is not affiliated with, endorsed by, or sponsored by the Department of Defense, Department of Veterans Affairs, or any branch of the U.S. Armed Forces or government organization.",
};

export const PERSONALITY = {
  headline: "Financial guidance does not have to sound like a spreadsheet.",
  copy: "Choose the communication style that fits you.",
  note: "The selected personality changes Renn\u2019s tone, not the financial calculations, recommendation rules, or safety standards.",
  options: [
    { name: "The Straight Shooter", desc: "Direct, no fluff, straight to the point." },
    { name: "The Calm Strategist", desc: "Measured, thoughtful, and steady." },
    { name: "The Smartass", desc: "A little sharp, a little fun, still useful." },
    { name: "The Supportive Friend", desc: "Encouraging and patient in your corner." },
    { name: "The No-Nonsense Planner", desc: "Structured, disciplined, and organized." },
  ],
};

export const SECURITY = {
  headline: "Your financial data. Your control.",
  claims: [
    "No bank passwords required",
    "No Social Security number collection",
    "No complete account-number collection",
    "Secure authentication",
    "Encrypted connections",
    "Data export",
    "Notification controls",
    "In-app account deletion",
    "No sale of personal financial information for advertising",
  ],
  links: [
    { label: "Privacy Policy", href: "/legal/privacy" },
    { label: "Terms of Use", href: "/legal/terms" },
    { label: "Financial Disclaimer", href: "/legal/financial-disclaimer" },
    { label: "AI Disclosure", href: "/legal/ai-disclosure" },
    { label: "Account Deletion", href: "#" },
    { label: "Support", href: "#" },
  ],
};

export const BETA = {
  headline: "Help shape Renn before the public launch.",
  copy: "We are recruiting active-duty members, Reservists, National Guard members, veterans, military spouses, transitioning service members, and working families for a limited beta.",
  benefits: [
    "Early access to Renn",
    "Six months of Renn Plus after launch",
    "Founding-member status",
    "Early access to new features",
    "A direct role in improving the product",
  ],
  cta: "Apply for the Renn Beta",
};

export const FAQ = [
  { q: "What is Renn?", a: "Renn is a financial planning and decision-support product that reviews your financial profile, creates a WorthScore, and identifies the three highest-impact actions to focus on next." },
  { q: "What is WorthScore?", a: "WorthScore is a 0\u2013100 signal that summarizes several areas of your finances \u2014 like cash flow, emergency savings, debt, credit, retirement, and protection \u2014 into one clear number so you can see your momentum over time." },
  { q: "What are Renn Moves?", a: "Renn Moves are the three actions Renn believes deserve your attention first, listed in priority order with a starting action, estimated impact, and difficulty level." },
  { q: "Does Renn connect to my bank?", a: "No. Renn does not require bank passwords and does not collect complete account numbers. You share the information you choose, and it stays under your control." },
  { q: "What information does Renn collect?", a: "Renn collects the financial details you enter in your guided profile \u2014 things like income, expenses, savings, debt, and goals. It does not collect Social Security numbers or bank passwords." },
  { q: "Is Renn a financial adviser?", a: "No. Renn provides financial education, organization, planning, and decision-support tools. It is not individualized legal, tax, accounting, insurance, or investment advice." },
  { q: "Is Renn only for military users?", a: "No. Renn is built to understand military financial situations, but it is designed for veterans, military families, and working families alike. Built for military. Smart enough for everyone." },
  { q: "How does the beta work?", a: "We are recruiting a limited group of testers. Apply with a short form, and if selected you will get early access and a direct role in shaping the product before public launch." },
  { q: "Will beta testers be charged?", a: "No. Participating in the beta is free, and selected testers receive six months of Renn Plus after launch as a thank-you." },
  { q: "Can I delete my information?", a: "Yes. Renn includes in-app account deletion and data export, so you can remove your information whenever you choose." },
  { q: "What devices will Renn support?", a: "Renn is being built for iPhone and iPad, with responsive experiences designed for each device." },
  { q: "How does Ask Renn use my financial profile?", a: "Ask Renn references the profile you have saved to explain priorities, compare options, and walk through the reasoning behind your plan. You stay in control of every decision." },
];

export const FINAL = {
  headline: "Stop wondering what to do next.",
  copy: "See your financial position, understand what needs attention, and get the three actions that matter most.",
  primary: "Join the Renn Beta",
  secondary: "Explore the Demo",
};

export const FOOTER = {
  about: "Renn is a product of WorthGrid Technologies, LLC.",
  links: [
    { label: "About", href: "#" },
    { label: "Support", href: "#" },
    { label: "Privacy Policy", href: "/legal/privacy" },
    { label: "Terms of Use", href: "/legal/terms" },
    { label: "Financial Disclaimer", href: "/legal/financial-disclaimer" },
    { label: "AI Disclosure", href: "/legal/ai-disclosure" },
    { label: "Account Deletion", href: "#" },
    { label: "Beta Application", href: "#beta" },
    { label: "Sign In", href: "#" },
  ],
  disclaimer: "Renn provides financial education, organization, planning, and decision-support tools. Renn is not a bank, lender, broker, registered investment adviser, accounting firm, law firm, insurance provider, or credit-repair company.",
  copyright: "\u00A9 2026 WorthGrid Technologies, LLC. All rights reserved.",
};
